import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Button,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Form,
  Table,
  Typography,
  Divider,
  Space,
  Modal,
  Upload,
  message,
  Alert,
  Tag,
  Spin,
} from "antd";
import type { TableColumnsType } from "antd";
import {
  SaveOutlined,
  SendOutlined,
  EyeOutlined,
  DownloadOutlined,
  UploadOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ArrowLeftOutlined,
  CalculatorOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import { formatCurrency } from "../../utils/Utils";
import {
  RABill,
  RABillLine,
  RABillDeduction,
  RABillTotals,
} from "../../data/mockRABills";
import workOrderApis from "../../api/WorkOrderManagement/WorkOrderApis";
import RABillApis from "../../api/WorkOrderManagement/RABillApis";
import dropdownApis from "../../api/WorkOrderManagement/DropdownApis";

const { Option } = Select;
const { RangePicker } = DatePicker;
const { TextArea } = Input;
const { Title, Text } = Typography;

interface WorkLine {
  id: string;
  workLineNumber: string;
  workLineTitle: string;
  description: string;
  uom: string;
  contractQuantity: number;
  cumulativeApprovedQuantity: number;
  previousBilledQuantity: number;
  availableQuantity: number;
  thisBillQuantity: number;
  rate: number;
  lineSubtotal: number;
  gstPercentage: number;
  gstValue: number;
  lineTotal: number;
}

const CreateRABillDetails: React.FC = () => {
  const { woId } = useParams<{ woId: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [workOrder, setWorkOrder] = useState<any>(null);
  const [workLines, setWorkLines] = useState<WorkLine[]>([]);
  const [deductions, setDeductions] = useState<RABillDeduction[]>([]);
  const [totals, setTotals] = useState<RABillTotals | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [showValidation, setShowValidation] = useState(false);
  const [raBillNumber, setRABillNumber] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const [notes, setNotes] = useState("");
  const [statesList, setStatesList] = useState<any[]>([]);

  useEffect(() => {
    // Always load data for demo purposes, use woId if available or default
    loadWorkOrderDetails();
    generateRABillNumber();
    loadStatesList();
  }, []);

  const loadStatesList = async () => {
    try {
      const response = await dropdownApis.getStatesByCountry(1); // India country ID
      if (response.data) {
        setStatesList(response.data);
      }
    } catch (error) {
      console.error("Error loading states list:", error);
    }
  };

  useEffect(() => {
    if (workLines.length > 0 && deductions.length > 0) {
      recalculateTotals(workLines, deductions);
    }
  }, [workLines, deductions]);

  // Auto-populate form values when work order is loaded
  useEffect(() => {
    if (workOrder) {
      form.setFieldsValue({
        raBillNumber: raBillNumber,
        woNumber: workOrder.woNumber,
        contractor: workOrder.contractor,
        placeOfSupply: workOrder.placeOfSupply,
        contractorGSTIN: workOrder.contractorGSTIN,
        companyGSTIN: workOrder.companyGSTIN,
        taxRegime: "CGST/SGST",
      });
    }
  }, [workOrder, raBillNumber, form]);

  const generateRABillNumber = () => {
    const year = new Date().getFullYear();
    const sequence = Math.floor(Math.random() * 9999) + 1;
    setRABillNumber(`RA/${year}/URB/${sequence.toString().padStart(4, "0")}`);
  };

  const mapApiResponseToRABill = (apiData: any) => {
    const workOrder = {
      id: apiData.workOrderId,
      woNumber: apiData.workOrderNumber,
      contractor: apiData.contractorCompanyName,
      contractorId: apiData.contractorCompanyId,
      site: apiData.siteLocation,
      project: apiData.workOrderTitle,
      section: apiData.workOrderDescription,
      woTotalValue: apiData.grossValue || 0,
      cumulativeWorkValue: apiData.totalExecutedValue || 0,
      cumulativeWorkDone: apiData.progressPercent || 0,
      previousRABillsValue: apiData.totalBilledValue || 0,
      contractorGSTIN: apiData.contractorGSTIN || "",
      companyGSTIN: apiData.companyGSTIN || "",
      placeOfSupply: apiData.placeOfSupply || "",
    };

    const workLines: WorkLine[] = (apiData.workOrderLines || []).map(
      (line: any) => ({
        id: line.workOrderLineId,
        workLineNumber: line.workOrderLineNumber,
        workLineTitle: line.taskTitle,
        description: line.description,
        uom: line.uomName,
        contractQuantity: line.quantity || 0,
        cumulativeApprovedQuantity: line.totalExecutedQty || 0,
        previousBilledQuantity: line.totalBilledQty || 0,
        availableQuantity:
          (line.totalExecutedQty || 0) -
          (line.totalBilledQty || 0),
        thisBillQuantity: 0,
        rate: line.rate || 0,
        lineSubtotal: 0,
        gstPercentage: 18,
        gstValue: 0,
        lineTotal: 0,
      })
    );

    // Map payment conditions to deductions
    const deductions: RABillDeduction[] = (
      apiData.workOrderPaymentConditions || []
    ).map((condition: any, index: number) => ({
      id: `ded_${condition.workOrderPaymentConditionId}`,
      raBillId: "",
      type: condition.conditionType,
      amount: condition.amountValue,
      percentage: condition.percentValue || 0,
      remarks: condition.paymentConditionDescription || condition.remarks || "",
      maxAmount: condition.maxAmount || undefined,
      isManual: condition.calculationType === "Manual",
      calculationType: condition.calculationType || "Percentage",
      taxableBase: condition.taxableBase || "Pre-tax",
      tdsSection: condition.tdsSection || undefined,
      sectionDescription: condition.sectionDescription || undefined,
      thresholdAmount: condition.thresholdAmount || 0,
    }));

    return { workOrder, workLines, deductions };
  };

  const loadWorkOrderDetails = async () => {
    setLoading(true);
    try {
      const response: any = await workOrderApis.getWorkOrderById(woId);

      if (response.data) {
        const { workOrder, workLines, deductions } = mapApiResponseToRABill(
          response.data
        );
        setWorkOrder(workOrder);
        setWorkLines(workLines);
        setDeductions(deductions);

        // Initialize totals immediately
        const initialTotals: RABillTotals = {
          raBillId: "DRAFT",
          subtotalExclTax: 0,
          gstTotal: 0,
          grossTotal: 0,
          mobilizationRecovery: 0,
          retention: 0,
          onHold: 0,
          tds: 0,
          totalDeductions: 0,
          roundOff: 0,
          netPayable: 0,
        };
        setTotals(initialTotals);

        // Force recalculation after state is set
        setTimeout(() => {
          recalculateTotals(workLines, deductions);
        }, 0);
      } else {
        message.error("No work order data found");
      }
    } catch (error) {
      console.error("Error loading work order:", error);
      message.error("Failed to load work order details");
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (value: number | null, lineId: string) => {
    console.log("handleQuantityChange called:", { value, lineId, workLinesLength: workLines.length });
    
    if (value === null) value = 0;

    const updatedLines = workLines.map((line) => {
      if (line.id === lineId) {
        const newQuantity = value!;
        const subtotal = newQuantity * line.rate;
        const gstValue = subtotal * (line.gstPercentage / 100);
        const lineTotal = subtotal + gstValue;

        console.log("Updating line:", {
          lineId,
          newQuantity,
          rate: line.rate,
          subtotal,
          gstValue,
          lineTotal
        });

        return {
          ...line,
          thisBillQuantity: newQuantity,
          lineSubtotal: subtotal,
          gstValue: gstValue,
          lineTotal: lineTotal,
        };
      }
      return line;
    });

    setWorkLines(updatedLines);
    recalculateTotals(updatedLines, deductions);
  };

  const calculateTotals = () => {
    const subtotalExclTax = workLines.reduce(
      (sum, line) => sum + line.lineSubtotal,
      0
    );
    const gstTotal = workLines.reduce((sum, line) => sum + line.gstValue, 0);
    const grossTotal = subtotalExclTax + gstTotal;

    // Auto-calculate percentage-based deductions
    const updatedDeductions = deductions.map((ded) => {
      if (ded.type === "Retention" && ded.percentage) {
        return { ...ded, amount: subtotalExclTax * (ded.percentage / 100) };
      }
      if (ded.type === "TDS" && ded.percentage) {
        return { ...ded, amount: subtotalExclTax * (ded.percentage / 100) };
      }
      return ded;
    });
    setDeductions(updatedDeductions);

    const totalDeductions = updatedDeductions.reduce(
      (sum, ded) => sum + ded.amount,
      0
    );
    const netPayable = grossTotal - totalDeductions;

    const calculatedTotals: RABillTotals = {
      raBillId: "",
      subtotalExclTax,
      gstTotal,
      grossTotal,
      mobilizationRecovery:
        updatedDeductions.find((d) => d.type === "Mobilization")
          ?.amount || 0,
      retention:
        updatedDeductions.find((d) => d.type === "Retention")?.amount || 0,
      onHold: updatedDeductions.find((d) => d.type === "On-Hold")?.amount || 0,
      tds: updatedDeductions.find((d) => d.type === "TDS")?.amount || 0,
      totalDeductions,
      roundOff: 0,
      netPayable,
    };

    setTotals(calculatedTotals);
  };

  const validateRABill = (): string[] => {
    const errors: string[] = [];

    // Check if at least one line has quantity > 0
    const hasPositiveQuantity = workLines.some(
      (line) => line.thisBillQuantity > 0
    );
    if (!hasPositiveQuantity) {
      errors.push("At least one line must have This Bill Quantity > 0");
    }

    // Check quantity limits
    workLines.forEach((line) => {
      if (line.thisBillQuantity > line.availableQuantity) {
        errors.push(
          `This Bill Qty exceeds available quantity for line ${line.workLineNumber}`
        );
      }
    });

    // Check form validation
    const formValues = form.getFieldsValue();
    if (!formValues.raPeriodFrom || !formValues.raPeriodTo) {
      errors.push("RA Period From and To dates are required");
    }

    if (!formValues.paymentDueDate) {
      errors.push("Payment Due Date is required");
    }

    if (!formValues.contractorGSTIN) {
      errors.push("Contractor GSTIN is required");
    }

    return errors;
  };

  const handleValidate = () => {
    const errors = validateRABill();
    setValidationErrors(errors);
    setShowValidation(true);

    if (errors.length === 0) {
      message.success("Validation passed successfully!");
    }
  };

  const handleSaveDraft = async () => {
    try {
      setLoading(true);
      // Save draft logic here
      await new Promise((resolve) => setTimeout(resolve, 1000)); // Simulate API call
      message.success("RA Bill saved as draft successfully!");
    } catch (error) {
      message.error("Failed to save draft");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    const errors = validateRABill();
    if (errors.length > 0) {
      setValidationErrors(errors);
      setShowValidation(true);
      message.error("Please fix validation errors before submitting");
      return;
    }

    try {
      setLoading(true);
      const formValues = form.getFieldsValue();
      
      // Prepare RA Bill data for API
      const raBillData = {
        workOrderId: parseInt(woId as string),
        raPeriodFrom: formValues.raPeriodFrom ? dayjs(formValues.raPeriodFrom).format('YYYY-MM-DD') : '',
        raPeriodTo: formValues.raPeriodTo ? dayjs(formValues.raPeriodTo).format('YYYY-MM-DD') : '',
        paymentDueDate: formValues.paymentDueDate ? dayjs(formValues.paymentDueDate).format('YYYY-MM-DD') : '',
        placeOfSupply: formValues.placeOfSupply || workOrder?.placeOfSupply || '',
        contractorGstin: formValues.contractorGSTIN || workOrder?.contractorGSTIN || '',
        companyGstin: formValues.companyGSTIN || workOrder?.companyGSTIN || '',
        taxRegime: formValues.taxRegime || 'CGST/SGST',
        tdsSection: deductions.find(d => d.type === 'TDS')?.tdsSection || '194C',
        tdsRate: deductions.find(d => d.type === 'TDS')?.percentage || 0,
        mobilizationRecovery: deductions.find(d => d.type === 'Mobilization')?.amount || 0,
        retentionAmount: deductions.find(d => d.type === 'Retention')?.amount || 0,
        onHoldAmount: deductions.find(d => d.type === 'On-Hold')?.amount || 0,
        notes: notes || '',
        isDraft: false,
        raBillLines: workLines
          .filter(line => line.thisBillQuantity > 0)
          .map(line => ({
            workOrderLineId: parseInt(line.id),
            billedQuantity: line.thisBillQuantity,
            remarks: `${line.workLineTitle} - Billed quantity: ${line.thisBillQuantity} ${line.uom}`
          })),
        filesData: uploadedFiles.map((file, index) => ({
          order: index,
          module: "Work Order Management",
          moduleFileType: index === 0 ? "Measurement Sheet" : "Site Photos"
        }))
      };

      console.log("Submitting RA Bill data:", raBillData);

      // Create FormData for multipart request
      const formData = new FormData();
      formData.append("raBill", JSON.stringify(raBillData));
      
      // Append files if any
      uploadedFiles.forEach((file, index) => {
        formData.append("files", file);
      });

      const response:any = await RABillApis.createRABill(formData);

      if (response.data) {
        Swal.fire({
          icon: "success",
          title: "RA Bill Submitted!",
          text: `RA Bill has been submitted for approval successfully`,
          confirmButtonText: "OK",
        }).then(() => {
          navigate(-1);
        });
      } else {
        throw new Error('Failed to create RA Bill');
      }
    } catch (error) {
      console.error("Error submitting RA Bill:", error);
      message.error("Failed to submit RA Bill");
    } finally {
      setLoading(false);
    }
  };

  const handleDeductionChange = (value: number | null, deductionId: string) => {
    if (value === null) value = 0;

    const updatedDeductions = deductions.map((ded) => {
      if (ded.id === deductionId) {
        // Apply business rules based on deduction type
        let validatedAmount = value!;

        if (ded.type === "Mobilization") {
          // Cannot exceed remaining mobilization balance
          const maxAmount = ded.maxAmount || 0;
          if (value! > maxAmount) {
            message.warning(
              `Mobilization Recovery cannot exceed remaining balance of ₹${maxAmount.toLocaleString()}`
            );
            validatedAmount = maxAmount;
          }
        }

        return { ...ded, amount: validatedAmount };
      }
      return ded;
    });

    setDeductions(updatedDeductions);
    recalculateTotals(workLines, updatedDeductions);
  };

  const recalculateTotals = (
    lines: WorkLine[],
    currentDeductions: RABillDeduction[]
  ) => {
    console.log("recalculateTotals called with lines:", lines.length, "deductions:", currentDeductions.length);
    
    const subtotalExclTax = lines.reduce(
      (sum, line) => sum + (line.lineSubtotal || 0),
      0
    );
    const gstTotal = lines.reduce((sum, line) => sum + (line.gstValue || 0), 0);
    const grossTotal = subtotalExclTax + gstTotal;
    
    console.log("Calculated totals:", { subtotalExclTax, gstTotal, grossTotal });

    // Calculate auto deductions with business rules
    const updatedDeductions = currentDeductions.map((deduction) => {
      if (!deduction.isManual) {
        let calculatedAmount = 0;

        if (deduction.type === "Retention") {
          // Calculate based on pre-tax or post-tax configuration
          const base =
            deduction.taxableBase === "Post-tax" ? grossTotal : subtotalExclTax;
          calculatedAmount = (base * (deduction.percentage || 0)) / 100;
        } else if (deduction.type === "On-Hold") {
          // Calculate based on flat or percentage
          if (deduction.calculationType === "Percentage") {
            calculatedAmount =
              (subtotalExclTax * (deduction.percentage || 0)) / 100;
          } else {
            // Flat amount - use configured amount
            calculatedAmount = deduction.amount || 0;
          }
        } else if (deduction.type === "TDS") {
          // Calculate TDS based on taxable base and threshold
          let taxableBase = 0;
          if (deduction.taxableBase === "Subtotal") {
            taxableBase = subtotalExclTax;
          } else if (deduction.taxableBase === "Gross Total") {
            taxableBase = grossTotal;
          }

          // Apply TDS only if above threshold amount
          if (taxableBase > (deduction.thresholdAmount || 0)) {
            calculatedAmount =
              (taxableBase * (deduction.percentage || 0)) / 100;
          } else {
            calculatedAmount = 0;
          }
        }

        return { ...deduction, amount: calculatedAmount };
      }
      return deduction;
    });

    setDeductions(updatedDeductions);

    const totalDeductions = updatedDeductions.reduce(
      (sum, ded) => sum + (ded.amount || 0),
      0
    );
    const netPayable = grossTotal - totalDeductions;

    // Extract individual deduction amounts for the totals interface
    const mobilizationRecovery =
      updatedDeductions.find((d) => d.type === "Mobilization")
        ?.amount || 0;
    const retention =
      updatedDeductions.find((d) => d.type === "Retention")?.amount || 0;
    const onHold =
      updatedDeductions.find((d) => d.type === "On-Hold")?.amount || 0;
    const tds = updatedDeductions.find((d) => d.type === "TDS")?.amount || 0;

    const finalTotals = {
      raBillId: raBillNumber || "DRAFT",
      subtotalExclTax,
      gstTotal,
      grossTotal,
      mobilizationRecovery,
      retention,
      onHold,
      tds,
      totalDeductions,
      roundOff: 0, // Add round-off logic if needed
      netPayable,
    };

    setTotals(finalTotals);
  };

  // Work Lines Table Columns
  const workLinesColumns: TableColumnsType<WorkLine> = useMemo(
    () => [
      {
        title: "Work Line #",
        dataIndex: "workLineNumber",
        key: "workLineNumber",
        width: 120,
        fixed: "left",
        render: (text) => <Text strong>{text}</Text>,
      },
      {
        title: "Work Line Title",
        dataIndex: "workLineTitle",
        key: "workLineTitle",
        width: 200,
        render: (text) => <Text strong>{text}</Text>,
      },
      {
        title: "Description",
        key: "description",
        width: 200,
        render: (_, record) => (
          <div className="text-muted small">{record.description}</div>
        ),
      },
      {
        title: "UOM",
        dataIndex: "uom",
        key: "uom",
        width: 80,
        align: "center",
      },
      {
        title: "Contract Qty",
        dataIndex: "contractQuantity",
        key: "contractQuantity",
        width: 120,
        align: "right",
        render: (qty) => qty.toLocaleString(),
      },
      {
        title: "Cum Approved Qty",
        dataIndex: "cumulativeApprovedQuantity",
        key: "cumulativeApprovedQuantity",
        width: 130,
        align: "right",
        render: (qty) => (
          <Text type="success" strong>
            {qty.toLocaleString()}
          </Text>
        ),
      },
      {
        title: "Prev Billed Qty",
        dataIndex: "previousBilledQuantity",
        key: "previousBilledQuantity",
        width: 120,
        align: "right",
        render: (qty) => <Text type="secondary">{qty.toLocaleString()}</Text>,
      },
      {
        title: "Available to Bill",
        dataIndex: "availableQuantity",
        key: "availableQuantity",
        width: 120,
        align: "right",
        render: (qty) => (
          <Text strong style={{ color: "#1890ff" }}>
            {qty.toLocaleString()}
          </Text>
        ),
      },
      {
        title: "This Bill Qty",
        key: "thisBillQuantity",
        width: 130,
        align: "center",
        render: (_, record) => (
          <InputNumber
            min={0}
            max={record.availableQuantity > 0 ? record.availableQuantity : undefined}
            precision={2}
            value={record.thisBillQuantity}
            onChange={(value) => handleQuantityChange(value, record.id)}
            style={{ width: "100%" }}
            placeholder="Enter quantity"
          />
        ),
      },
      {
        title: "Rate",
        dataIndex: "rate",
        key: "rate",
        width: 100,
        align: "right",
        render: (rate) => `₹${rate.toLocaleString()}`,
      },
      {
        title: "Line Subtotal",
        dataIndex: "lineSubtotal",
        key: "lineSubtotal",
        width: 130,
        align: "right",
        render: (amount) => <Text strong>{formatCurrency(amount, "INR")}</Text>,
      },
      {
        title: "GST %",
        dataIndex: "gstPercentage",
        key: "gstPercentage",
        width: 80,
        align: "center",
        render: (pct) => `${pct}%`,
      },
      {
        title: "GST Value",
        dataIndex: "gstValue",
        key: "gstValue",
        width: 120,
        align: "right",
        render: (amount) => (
          <Text type="warning">{formatCurrency(amount, "INR")}</Text>
        ),
      },
      {
        title: "Line Total",
        dataIndex: "lineTotal",
        key: "lineTotal",
        width: 130,
        align: "right",
        render: (amount) => (
          <Text strong style={{ color: "#52c41a" }}>
            {formatCurrency(amount, "INR")}
          </Text>
        ),
      },
    ],
    [workLines]
  );

  if (loading && !workOrder) {
    return (
      <Content>
        <div style={{ textAlign: "center", marginTop: "20vh" }}>
          <Spin size="large" />
          <div className="mt-3">Loading work order details...</div>
        </div>
      </Content>
    );
  }

  return (
    <Content>
      <Head title="Create RA Bill" />

      {/* Header */}
      <div className="nk-content-inner">
        <div className="nk-content-body">
          <div className="nk-block-head nk-block-head-sm">
            <div className="nk-block-between g-3">
              <div className="nk-block-head-content">
                <Title level={3} className="nk-block-title">
                  Create RA Bill
                </Title>
                <div className="nk-block-des text-soft">
                  <p>
                    Work Order: {workOrder?.woNumber} | Contractor:{" "}
                    {workOrder?.contractor}
                  </p>
                </div>
              </div>
              <div className="nk-block-head-content">
                <Space>
                  <Button
                    icon={<ArrowLeftOutlined />}
                    onClick={() =>
                      navigate(`${process.env.PUBLIC_URL}/ra-bills-create`)
                    }
                    style={{ marginRight: 16 }}
                  >
                    Back
                  </Button>
                  {/* <Button icon={<EyeOutlined />}>Preview PDF</Button>
                  <Button icon={<DownloadOutlined />}>Export Lines</Button> */}
                </Space>
              </div>
            </div>
          </div>

          {/* Validation Errors */}
          {showValidation && validationErrors.length > 0 && (
            <Alert
              type="error"
              showIcon
              closable
              message="Validation Errors"
              description={
                <ul style={{ marginBottom: 0 }}>
                  {validationErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              }
              onClose={() => setShowValidation(false)}
              style={{ marginBottom: 16 }}
            />
          )}

          {/* Header Details */}
          <Card title="RA Bill Header" className="mb-4">
            <Form form={form} layout="vertical">
              <Row gutter={[24, 16]}>
                {/* <Col xs={24} lg={8}>
                  <Form.Item label="RA Bill Number" name="raBillNumber">
                    <Input value={raBillNumber} disabled />
                  </Form.Item>
                </Col> */}
                <Col xs={24} lg={8}>
                  <Form.Item label="Work Order Number" name="woNumber">
                    <Input value={workOrder?.woNumber} disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={8}>
                  <Form.Item label="Contractor" name="contractor">
                    <Input value={workOrder?.contractor} disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={8}>
                  <Form.Item
                    label="RA Period From"
                    name="raPeriodFrom"
                    rules={[{ required: true }]}
                  >
                    <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={8}>
                  <Form.Item
                    label="RA Period To"
                    name="raPeriodTo"
                    rules={[{ required: true }]}
                  >
                    <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={8}>
                  <Form.Item
                    label="Payment Due Date"
                    name="paymentDueDate"
                    rules={[{ required: true }]}
                  >
                    <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={8}>
                  <Form.Item label="Place of Supply" name="placeOfSupply">
                    <Select 
                      defaultValue={workOrder?.placeOfSupply}
                      placeholder="Select place of supply"
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input:any, option:any) =>
                        (option?.children)?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                    >
                      {statesList.map((state) => (
                        <Option key={state.id} value={state.stateName}>
                          {state.stateName}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} lg={8}>
                  <Form.Item
                    label="Contractor GSTIN"
                    name="contractorGSTIN"
                    rules={[{ required: true }]}
                  >
                    <Input defaultValue={workOrder?.contractorGSTIN} />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={8}>
                  <Form.Item label="Company GSTIN" name="companyGSTIN">
                    <Input value={workOrder?.companyGSTIN} disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={8}>
                  <Form.Item label="Tax Regime" name="taxRegime">
                    <Select defaultValue="CGST/SGST">
                      <Option value="Cgst Sgst">CGST/SGST</Option>
                      <Option value="Igst">IGST</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Form>

            {/* Commercial Summary */}
            <Divider>Commercial Summary</Divider>
            <Row gutter={[24, 16]}>
              <Col xs={12} lg={4}>
                <div className="text-center p-3 border rounded">
                  <div className="text-muted small">WO Total Value</div>
                  <div className="fw-bold h5 mb-0">
                    {formatCurrency(workOrder?.woTotalValue || 0, "INR")}
                  </div>
                </div>
              </Col>
              <Col xs={12} lg={4}>
                <div className="text-center p-3 border rounded">
                  <div className="text-muted small">Cumulative Work Done %</div>
                  <div className="fw-bold h5 mb-0 text-success">
                    {workOrder?.cumulativeWorkDone || 0}%
                  </div>
                </div>
              </Col>
              <Col xs={12} lg={4}>
                <div className="text-center p-3 border rounded">
                  <div className="text-muted small">Cumulative Work Value</div>
                  <div className="fw-bold h5 mb-0">
                    {formatCurrency(workOrder?.cumulativeWorkValue || 0, "INR")}
                  </div>
                </div>
              </Col>
              <Col xs={12} lg={4}>
                <div className="text-center p-3 border rounded">
                  <div className="text-muted small">
                    Previous RA Bills Value
                  </div>
                  <div className="fw-bold h5 mb-0 text-info">
                    {formatCurrency(
                      workOrder?.previousRABillsValue || 0,
                      "INR"
                    )}
                  </div>
                </div>
              </Col>
              <Col xs={12} lg={4}>
                <div className="text-center p-3 border rounded">
                  <div className="text-muted small">Current Bill Value</div>
                  <div className="fw-bold h5 mb-0 text-primary">
                    {formatCurrency(totals?.netPayable || 0, "INR")}
                  </div>
                </div>
              </Col>
              <Col xs={12} lg={4}>
                <div className="text-center p-3 border rounded">
                  <div className="text-muted small">
                    Balance Works for RA Bill
                  </div>
                  <div className="fw-bold h5 mb-0">
                    {formatCurrency(
                      (workOrder?.cumulativeWorkValue || 0) -
                        (workOrder?.previousRABillsValue || 0) -
                        (totals?.netPayable || 0),
                      "INR"
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          </Card>

          {/* Work Lines */}
          <Card title="Work Lines" className="mb-4">
            <Table
              columns={workLinesColumns}
              dataSource={workLines}
              rowKey="id"
              scroll={{ x: 1600 }}
              pagination={false}
              size="small"
            />

            {/* Real-time Calculations Display */}
            <div className="text-end mt-3">
              <div
                className="d-inline-block p-3 border rounded bg-light"
                style={{ minWidth: "300px" }}
              >
                <div className="fw-bold text-primary mb-2">Summary</div>
                {(() => {
                  // Calculate overall totals
                  const totalAvailable = workLines.reduce((sum, line) => {
                    const available =
                      (line.cumulativeApprovedQuantity || 0) -
                      (line.previousBilledQuantity || 0);
                    return sum + available;
                  }, 0);

                  const totalBillQty = workLines.reduce((sum, line) => {
                    return sum + (line.thisBillQuantity || 0);
                  }, 0);

                  const overallSubtotal = workLines.reduce((sum, line) => {
                    const qty = line.thisBillQuantity || 0;
                    const rate = line.rate || 0;
                    return sum + qty * rate;
                  }, 0);

                  const overallGSTValue = workLines.reduce((sum, line) => {
                    const qty = line.thisBillQuantity || 0;
                    const rate = line.rate || 0;
                    const gstPct = line.gstPercentage || 0;
                    const subtotal = qty * rate;
                    return sum + subtotal * (gstPct / 100);
                  }, 0);

                  const overallLineTotal = overallSubtotal + overallGSTValue;

                  const hasQuantities = workLines.some(
                    (line) => (line.thisBillQuantity || 0) > 0
                  );

                  if (!hasQuantities) {
                    return (
                      <div className="text-muted small">
                        Enter Bill Qty to see calculations
                      </div>
                    );
                  }

                  return (
                    <div className="p-2 border rounded bg-white">
                      <div className="small">
                        <div className="d-flex justify-content-between mb-1">
                          <span>Total Available:</span>
                          <span className="fw-bold">
                            {totalAvailable.toLocaleString()}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between mb-1">
                          <span>Total Bill Qty:</span>
                          <span className="fw-bold text-primary">
                            {totalBillQty.toLocaleString()}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between mb-1">
                          <span>Subtotal:</span>
                          <span className="fw-bold">
                            ₹{overallSubtotal.toLocaleString()}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between mb-1">
                          <span>GST Value:</span>
                          <span className="fw-bold text-warning">
                            ₹{overallGSTValue.toLocaleString()}
                          </span>
                        </div>
                        <hr className="my-2" />
                        <div className="d-flex justify-content-between">
                          <span className="fw-bold">Line Total:</span>
                          <span className="fw-bold text-success">
                            ₹{overallLineTotal.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </Card>

          {/* Deductions */}
          <Card title="Deductions & Recoveries" className="mb-4">
            <Row gutter={[24, 16]}>
              {deductions.map((deduction:any) => (
                <Col xs={24} lg={6} key={deduction.id}>
                  <div className="border rounded p-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="fw-bold">{deduction.type}</div>
                      {!deduction.isManual && <Tag color="blue">Auto</Tag>}
                    </div>

                    {/* Business Rule Info */}
                    <div
                      className="text-muted small mb-2"
                      style={{ minHeight: "32px" }}
                    >
                      {deduction.type === "Mobilization" && (
                        <div>
                          <div>
                            Max: ₹{(deduction.amount || 0).toLocaleString()}
                          </div>
                          <div className="text-info">
                            Remaining balance limit
                          </div>
                        </div>
                      )}
                      {deduction.type === "Retention" && (
                        <div>
                          <div>
                            @ {deduction.percentage}% on {deduction.taxableBase}
                          </div>
                          <div className="text-info">Auto calculated</div>
                        </div>
                      )}
                      {deduction.type === "On-Hold" && (
                        <div>
                          <div>
                            {deduction.calculationType === "Percentage"
                              ? `@ ${deduction.percentage}% on Subtotal`
                              : "Flat amount"}
                          </div>
                          <div className="text-info">WO policy based</div>
                        </div>
                      )}
                      {deduction.type === "TDS" && (
                        <div>
                          <div>
                            @ {deduction.percentage}% ({deduction.tdsSection})
                          </div>
                          <div className="text-info">
                            On {deduction.taxableBase}
                          </div>
                          <div className="text-warning small">
                            Above ₹
                            {(deduction.thresholdAmount || 0).toLocaleString()}
                          </div>
                        </div>
                      )}
                    </div>

                    <InputNumber
                      style={{ width: "100%" }}
                      value={deduction.amount}
                      onChange={(value) =>
                        handleDeductionChange(value, deduction.id)
                      }
                      formatter={(value) =>
                        `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      }
                      parser={(value) =>
                        value!.replace(/₹\s?|(,*)/g, "") as any
                      }
                      disabled={
                        deduction.type === "Retention" ||
                        deduction.type === "TDS" ||
                        (deduction.type === "On-Hold" &&
                          deduction.calculationType === "Percentage")
                      }
                      max={
                        deduction.type === "Mobilization"
                          ? deduction.maxAmount
                          : undefined
                      }
                    />

                    {deduction.remarks && (
                      <div
                        className="text-muted small mt-2"
                        style={{ fontSize: "11px" }}
                      >
                        {deduction.remarks}
                      </div>
                    )}
                  </div>
                </Col>
              ))}
            </Row>
          </Card>

          {/* Totals */}
          <Card title="Bill Totals" className="mb-4">
            <Row gutter={[24, 16]} className="text-center">
              <Col xs={12} lg={3}>
                <div className="border rounded p-3">
                  <div className="text-muted small">Subtotal (excl. tax)</div>
                  <div className="fw-bold h5 mb-0 mt-3">
                    {formatCurrency(totals?.subtotalExclTax || 0, "INR")}
                  </div>
                </div>
              </Col>
              <Col xs={12} lg={3}>
                <div className="border rounded p-3">
                  <div className="text-muted small">GST Total</div>
                  <div className="fw-bold h5 mb-0 mt-3 text-warning">
                    {formatCurrency(totals?.gstTotal || 0, "INR")}
                  </div>
                </div>
              </Col>
              <Col xs={12} lg={3}>
                <div className="border rounded p-3">
                  <div className="text-muted small">Gross Total</div>
                  <div className="fw-bold h5 mb-0 mt-3">
                    {formatCurrency(totals?.grossTotal || 0, "INR")}
                  </div>
                </div>
              </Col>
              <Col xs={12} lg={3}>
                <div className="border rounded p-3">
                  <div className="text-muted small">Total Deductions</div>
                  <div className="fw-bold h5 mb-0 text-danger mt-3">
                    -{formatCurrency(totals?.totalDeductions || 0, "INR")}
                  </div>
                </div>
              </Col>
            </Row>
            <Divider />
            <div className="">
              <div
                className="fw-bold text-success"
                style={{ fontSize: "20px" }}
              >
                Net Payable: {formatCurrency(totals?.netPayable || 0, "INR")}
              </div>
            </div>
          </Card>

          {/* Notes & Attachments */}
          <Card title="Notes & Attachments" className="mb-4">
            <Row gutter={[24, 16]}>
              <Col xs={24} lg={12}>
                <Form.Item label="Notes">
                  <TextArea
                    rows={4}
                    placeholder="Enter any additional notes..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} lg={12}>
                <Form.Item label="Attachments">
                  <Upload.Dragger
                    multiple
                    beforeUpload={(file) => {
                      setUploadedFiles(prev => [...prev, file]);
                      return false; // Prevent automatic upload
                    }}
                    onRemove={(file) => {
                      setUploadedFiles(prev => prev.filter(f => f.uid !== file.uid));
                    }}
                    fileList={uploadedFiles.map(file => ({
                      uid: file.uid,
                      name: file.name,
                      status: 'done' as const
                    }))}
                  >
                    <p className="ant-upload-drag-icon">
                      <UploadOutlined />
                    </p>
                    <p className="ant-upload-text">
                      Click or drag files to this area to upload
                    </p>
                    <p className="ant-upload-hint">
                      Support for measurement sheets, photos, and other
                      supporting documents
                    </p>
                  </Upload.Dragger>
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Action Buttons */}
          <Card>
            <div className="text-end">
              <Space size="large">
                <Button
                  size="large"
                  icon={<SaveOutlined />}
                  onClick={handleSaveDraft}
                  loading={loading}
                >
                  Save Draft
                </Button>
                <Button
                  size="large"
                  icon={<CheckCircleOutlined />}
                  onClick={handleValidate}
                >
                  Validate
                </Button>
                <Button
                  type="primary"
                  size="large"
                  icon={<SendOutlined />}
                  onClick={handleSubmit}
                  loading={loading}
                  disabled={validationErrors.length > 0}
                >
                  Submit for Approval
                </Button>
              </Space>
            </div>
          </Card>
        </div>
      </div>
    </Content>
  );
};

export default CreateRABillDetails;
